// AI Enhancement Service — Witty Sidekick
// Produces short, punchy one-liners for notification feedback

import store from '@/store';
import { passesToneGuard } from '@/utils/toneGuard';
import { buildEnhancementPrompt } from '../constants/aiPrompts';
import openaiService from './openai';
import ProgramContextService from './programContextService';

interface AIEnhancementRequest {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  context?: {
    action?: string;
    data?: any;
    userType?: string;
    adkType?: string;
    metadata?: Record<string, any>;
    user?: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
    programContext?: {
      programId?: string;
      programName?: string;
      programDesc?: string;
      pathways?: string[];
      industries?: string[];
    };
  };
}

interface AIEnhancementResponse {
  enhancedMessage: string;
  success: boolean;
  error?: string;
  processingTime?: number;
}

// Every toast used to be an OpenAI round-trip: after ~14 quick actions the per-user
// server rate limit (20 / 5 min) tripped, the bubble stayed blank while awaiting, and
// the tone drifted. Cache per message type, back off after a failure, cap the wait,
// and never show copy that fails the tone guard - the plain message is always right.
const CACHE_PREFIX = 'ai-toast:';
const COOLDOWN_KEY = 'ai-toast:cooldown-until';
const CACHE_TTL_MS = 30 * 60 * 1000;
const COOLDOWN_MS = 5 * 60 * 1000;
const MAX_WAIT_MS = 2500;

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const cacheKey = (request: AIEnhancementRequest): string => {
  const c = request.context || {};
  return `${CACHE_PREFIX}${c.action || ''}|${request.type}|${c.adkType || ''}|${request.message}`;
};

const readCache = (key: string): string | null => {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { text: string; at: number };
    if (Date.now() - entry.at > CACHE_TTL_MS) {
      s.removeItem(key);
      return null;
    }
    return entry.text;
  } catch {
    return null;
  }
};

const writeCache = (key: string, text: string): void => {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify({ text, at: Date.now() }));
  } catch {
    // quota / private mode: caching is best-effort
  }
};

const inCooldown = (): boolean => {
  const s = storage();
  if (!s) return false;
  const until = Number(s.getItem(COOLDOWN_KEY) || 0);
  return until > Date.now();
};

const startCooldown = (): void => {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(COOLDOWN_KEY, String(Date.now() + COOLDOWN_MS));
  } catch {
    // ignore
  }
};

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('AI enhancement timed out')), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

class AIEnhancementService {
  private static stats = {
    total: 0,
    success: 0,
    failed: 0,
    cached: 0,
    skipped: 0,
    totalMs: 0
  };

  public static async enhanceNotification(
    request: AIEnhancementRequest
  ): Promise<AIEnhancementResponse> {
    const startTime = Date.now();
    this.stats.total++;

    const key = cacheKey(request);
    const cached = readCache(key);
    if (cached) {
      this.stats.cached++;
      return { enhancedMessage: cached, success: true, processingTime: 0 };
    }
    if (inCooldown()) {
      this.stats.skipped++;
      return {
        enhancedMessage: request.message,
        success: false,
        error: 'AI enhancement cooling down',
        processingTime: 0
      };
    }

    try {
      const { message, type, context } = request;

      // Auto-include program context if user is in a program
      let enhancedContext = { ...context };
      if (ProgramContextService.isUserInProgram()) {
        const pc = ProgramContextService.getCurrentProgramContext();
        enhancedContext = {
          ...context,
          programContext: {
            programId: pc.programId,
            programName: pc.programName,
            programDesc: pc.programDesc,
            pathways: pc.pathways,
            industries: pc.industries
          }
        };
      }

      // Auto-include user info from store if not provided
      if (!enhancedContext.user) {
        const storeUser = (store.state as any)?.user?.user;
        if (storeUser) {
          enhancedContext.user = {
            firstName: storeUser.firstName,
            lastName: storeUser.lastName,
            fullName:
              storeUser.fullName ||
              `${storeUser.firstName || ''} ${storeUser.lastName || ''}`.trim()
          };
        }
      }

      // Build prompt pair
      const { systemPrompt, userPrompt } = buildEnhancementPrompt(message, type, enhancedContext);

      // Get userId for rate limiting
      const userId = (store.state as any)?.user?.user?._id;

      const enhancedMessage = await withTimeout(
        openaiService.generateScope(userPrompt, systemPrompt, userId),
        MAX_WAIT_MS
      );

      // Post-process: strip quotes, replace leftover placeholders
      let clean = enhancedMessage.replace(/^["']|["']$/g, '');

      if (enhancedContext?.user) {
        const name =
          enhancedContext.user.fullName ||
          `${enhancedContext.user.firstName || ''} ${enhancedContext.user.lastName || ''}`.trim();
        if (name) {
          clean = clean
            .replace(/\[NAME\]/gi, name)
            .replace(/\[participant\]/gi, name)
            .replace(/\[user\]/gi, name)
            .replace(/\[student\]/gi, name);
        }
      }

      const processingTime = Date.now() - startTime;

      if (!passesToneGuard(clean)) {
        this.stats.failed++;
        return {
          enhancedMessage: request.message,
          success: false,
          error: 'AI enhancement failed the tone guard',
          processingTime
        };
      }

      this.stats.success++;
      this.stats.totalMs += processingTime;
      writeCache(key, clean);

      return {
        enhancedMessage: clean,
        success: true,
        processingTime
      };
    } catch (error) {
      this.stats.failed++;
      // Rate limit / timeout / network: stop calling for a while instead of failing
      // (and blanking the bubble) on every toast for the rest of the session.
      startCooldown();

      return {
        enhancedMessage: request.message,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        processingTime: Date.now() - startTime
      };
    }
  }

  public static getAnalytics() {
    return {
      ...this.stats,
      avgMs: this.stats.success > 0 ? Math.round(this.stats.totalMs / this.stats.success) : 0,
      successRate:
        this.stats.total > 0
          ? `${((this.stats.success / this.stats.total) * 100).toFixed(1)}%`
          : '0%'
    };
  }

  public static resetAnalytics() {
    this.stats = { total: 0, success: 0, failed: 0, totalMs: 0 };
  }
}

export default AIEnhancementService;
